import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react'
import styles from './ChipRail.module.css'

export interface ChipRailItem {
  key: string
  /** Small line above the label: what it is and when — "League · In season". */
  eyebrow?: ReactNode
  label: ReactNode
  /**
   * Sits before the eyebrow and label, centred on the chip — a score ring, a
   * game logo. Decorative unless it carries its own accessible name.
   */
  leading?: ReactNode
}

export interface ChipRailProps {
  items: ChipRailItem[]
  selected: string
  onSelect: (key: string) => void
  /** Names the set for screen readers: "Competition". */
  'aria-label': string
  /** The selected chip's colour — an org passes its own. */
  accent?: string
  /**
   * Chips held still at the start, before a divider, while the rest scroll —
   * an "everything" choice that should never slide out of reach. They are part
   * of the same set: one selection, one run of arrow keys.
   */
  pinned?: ChipRailItem[]
  /**
   * Rides at the end of the scrolling row, after the last chip — an "Add"
   * link, say. Not a choice, so it is not one of the tabs' arrow-key stops.
   */
  trailing?: ReactNode
  className?: string
}

const NO_PINNED: ChipRailItem[] = []

/**
 * A row of choices that runs off the edge when there are more than fit.
 *
 * Drag it, swipe it, or use the arrows, which appear only when there is
 * somewhere to go; the chips fade out at an edge with more behind it. Each chip is a
 * tab — arrow keys move along the row, and only the selected chip sits in the
 * tab order.
 */
export function ChipRail({ items, selected, onSelect, 'aria-label': label, accent, pinned = NO_PINNED, trailing, className }: ChipRailProps) {
  const idPrefix = useId()
  const all = [...pinned, ...items]
  const track = useRef<HTMLDivElement>(null)
  const chips = useRef<(HTMLButtonElement | null)[]>([])
  const drag = useRef<{ x: number; scroll: number; moved: boolean } | null>(null)
  const dragged = useRef(false)
  const [edges, setEdges] = useState({ left: false, right: false })

  const measure = useCallback(() => {
    const t = track.current
    if (!t) return
    const left = t.scrollLeft > 2
    const right = t.scrollLeft + t.clientWidth < t.scrollWidth - 2
    setEdges((e) => (e.left === left && e.right === right ? e : { left, right }))
  }, [])

  useEffect(() => {
    measure()
    const t = track.current
    if (!t || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(t)
    return () => ro.disconnect()
  }, [measure, items.length, trailing])

  // The selected chip is always in view: a rail that opens on week 1 while
  // week 5 is selected hides the one thing it's showing.
  useEffect(() => {
    const t = track.current
    // A pinned chip never scrolls, so only the scrolling ones are looked for.
    const at = items.findIndex((i) => i.key === selected)
    const chip = at >= 0 ? chips.current[pinned.length + at] : undefined
    if (!t || !chip) return
    const left = chip.offsetLeft - t.offsetLeft
    if (left < t.scrollLeft || left + chip.offsetWidth > t.scrollLeft + t.clientWidth) {
      t.scrollLeft = Math.max(0, left - 16)
    }
  }, [selected, items, pinned.length])

  // The arrows only render alongside the track, so it is always there.
  const page = (dir: -1 | 1) => {
    const t = track.current!
    t.scrollBy({ left: dir * t.clientWidth * 0.8, behavior: 'smooth' })
  }

  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0 || !track.current) return
    drag.current = { x: e.clientX, scroll: track.current.scrollLeft, moved: false }
  }
  const onMouseMove = (e: MouseEvent) => {
    const d = drag.current
    if (!d || !track.current) return
    const dx = e.clientX - d.x
    if (Math.abs(dx) > 3) d.moved = true
    track.current.scrollLeft = d.scroll - dx
  }
  const endDrag = () => {
    if (drag.current) dragged.current = drag.current.moved
    drag.current = null
  }
  // A drag that ends over a chip must not also choose it.
  const onClickCapture = (e: MouseEvent) => {
    if (dragged.current) {
      e.preventDefault()
      e.stopPropagation()
      dragged.current = false
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    const n = all.length
    const to = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : step ? (i + step + n) % n : -1
    if (to < 0) return
    e.preventDefault()
    onSelect(all[to].key)
    const chip = chips.current[to]
    chip?.focus()
    chip?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }

  const style = accent ? ({ '--chip-rail-accent': accent } as CSSProperties) : undefined
  const overflows = edges.left || edges.right
  const chipId = (i: number) => `${idPrefix}-chip-${i}`

  const renderChip = (item: ChipRailItem, i: number) => {
    const isSelected = item.key === selected
    const text = (
      <>
        {item.eyebrow && <span className={styles.eyebrow}>{item.eyebrow}</span>}
        <span className={styles.label}>{item.label}</span>
      </>
    )
    return (
      <button
        key={item.key}
        id={chipId(i)}
        ref={(el) => { chips.current[i] = el }}
        type="button"
        role="tab"
        aria-selected={isSelected}
        tabIndex={isSelected ? 0 : -1}
        className={[styles.chip, isSelected && styles.selected, item.leading != null && styles.withLeading].filter(Boolean).join(' ')}
        onClick={() => onSelect(item.key)}
        onKeyDown={(e) => onKeyDown(e, i)}
      >
        {item.leading != null ? (
          <>
            <span className={styles.leading}>{item.leading}</span>
            <span className={styles.text}>{text}</span>
          </>
        ) : text}
      </button>
    )
  }

  return (
    <div className={[styles.rail, className].filter(Boolean).join(' ')} style={style}>
      {pinned.length > 0 && (
        <>
          <div className={styles.pinned}>{pinned.map((item, i) => renderChip(item, i))}</div>
          <span className={styles.divider} aria-hidden="true" />
        </>
      )}
      <div className={[styles.viewport, edges.left && styles.moreLeft, edges.right && styles.moreRight].filter(Boolean).join(' ')}>
        <div
          ref={track}
          role="tablist"
          aria-label={label}
          // Pinned chips live outside the scrolling track but belong to this
          // set; owning every chip in order keeps them first for a screen reader.
          aria-owns={pinned.length > 0 ? all.map((_, i) => chipId(i)).join(' ') : undefined}
          className={styles.track}
          onScroll={measure}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onClickCapture={onClickCapture}
        >
          {items.map((item, i) => renderChip(item, pinned.length + i))}
          {trailing != null && <span className={styles.trailing}>{trailing}</span>}
        </div>
      </div>
      {overflows && (
        <div className={styles.arrows}>
          <button type="button" className={styles.arrow} aria-label="Scroll left" disabled={!edges.left} onClick={() => page(-1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <button type="button" className={styles.arrow} aria-label="Scroll right" disabled={!edges.right} onClick={() => page(1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        </div>
      )}
    </div>
  )
}
